import { useRef, useState } from "react";
import { toast } from "sonner";
import { addDays, format, subDays } from "date-fns";
import { Lock, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAutosave } from "@/lib/autosave";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { AttachmentsPanel } from "@/components/core/AttachmentsPanel";
import { RichEditor, SaveIndicator } from "./RichEditor";

export const COLORS = ["#2B9BE8", "#0A1128", "#E8173A", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#64748B"];
const L = ({ children }) => <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">{children}</div>;
const DT = "yyyy-MM-dd'T'HH:mm";

const toForm = (e) => {
  const s = new Date(e.inizio), en = new Date(e.fine);
  return { titolo: e.titolo || "", all_day: !!e.all_day, colore: e.colore || COLORS[0], client_id: e.client_id || "none", note_html: e.note_html || "",
    start: e.all_day ? format(s, "yyyy-MM-dd") : format(s, DT),
    end: e.all_day ? format(subDays(en, 1) < s ? s : subDays(en, 1), "yyyy-MM-dd") : format(en, DT) };
};

const toPayload = (f) => {
  let s, e;
  if (f.all_day) { s = new Date(`${f.start}T00:00`); e = addDays(new Date(`${(f.end || f.start)}T00:00`), 1); }
  else { s = new Date(f.start); e = new Date(f.end); }
  if (!(e > s)) e = f.all_day ? addDays(s, 1) : new Date(s.getTime() + 3600e3);
  return { titolo: f.titolo.trim(), all_day: f.all_day, colore: f.colore, client_id: f.client_id === "none" ? null : f.client_id, note_html: f.note_html,
    inizio: s.toISOString(), fine: e.toISOString() };
};

const EventBody = ({ initial, clients, onChanged, onDeleted }) => {
  const [id, setId] = useState(initial.id || null);
  const [f, setF] = useState(() => toForm(initial));
  const [del, setDel] = useState(false);
  const creating = useRef(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const toggleAllDay = (v) => setF((p) => ({ ...p, all_day: v, start: v ? p.start.slice(0, 10) : `${p.start.slice(0, 10)}T09:00`, end: v ? p.end.slice(0, 10) : `${p.end.slice(0, 10)}T10:00` }));

  const { status } = useAutosave(f, async (data) => {
    if (!data.titolo.trim()) return false;
    const payload = toPayload(data);
    if (!id && !creating.current) {
      creating.current = api.post("/me/eventi", payload).then((r) => { setId(r.data.id); onChanged(r.data); return r.data.id; });
      await creating.current;
      return true;
    }
    const realId = id || (await creating.current);
    onChanged((await api.put(`/me/eventi/${realId}`, payload)).data);
    return true;
  });

  const remove = async () => {
    try { await api.delete(`/me/eventi/${id}`); toast.success("Evento eliminato"); onDeleted(id); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-center justify-between pr-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#E8173A]"><Lock className="h-3.5 w-3.5" />Evento personale · visibile solo a te</span>
        <SaveIndicator status={status} testid="pevent-save-status" />
      </div>
      <Input autoFocus value={f.titolo} onChange={set("titolo")} placeholder="Titolo dell'evento" className="rounded-xl text-xl font-extrabold h-12" data-testid="pevent-title" />
      <div className="flex flex-wrap items-end gap-3">
        <div><L>Inizio</L><Input type={f.all_day ? "date" : "datetime-local"} value={f.start} onChange={set("start")} className="rounded-xl" data-testid="pevent-start" /></div>
        <div><L>Fine</L><Input type={f.all_day ? "date" : "datetime-local"} value={f.end} onChange={set("end")} className="rounded-xl" data-testid="pevent-end" /></div>
        <label className="flex items-center gap-2 text-sm font-bold text-[#0A1128] pb-2">Tutto il giorno<Switch checked={f.all_day} onCheckedChange={toggleAllDay} className="data-[state=checked]:bg-[#E8173A] data-[state=unchecked]:bg-[#5FB3EA]" data-testid="pevent-allday" /></label>
      </div>
      <div className="flex flex-wrap items-end gap-6">
        <div><L>Colore</L><div className="flex gap-1.5">{COLORS.map((c) => (
          <button key={c} type="button" onClick={() => set("colore")(c)} className={`h-7 w-7 rounded-full transition-transform ${f.colore === c ? "ring-2 ring-offset-2 ring-[#0A1128] scale-110" : ""}`} style={{ background: c }} aria-label={c} data-testid={`pevent-color-${c.slice(1)}`} />
        ))}</div></div>
        <div className="min-w-[260px]"><L>Cliente collegato (facoltativo)</L>
          <Select value={f.client_id} onValueChange={set("client_id")}>
            <SelectTrigger className="rounded-xl" data-testid="pevent-client"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Nessun cliente</SelectItem>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex-1"><L>Appunti</L><RichEditor value={f.note_html} onChange={set("note_html")} minHeight={420} testid="pevent-notes" /></div>
      <AttachmentsPanel tipo="event" targetId={id} title="Allegati (privati, visibili solo a te)" testid="pevent-attachments" />
      {id && <div><Button variant="outline" onClick={() => setDel(true)} className="rounded-full font-bold text-[#E8173A] border-red-200" data-testid="pevent-delete"><Trash2 className="h-4 w-4 mr-1.5" />Elimina evento</Button></div>}
      <ConfirmDialog open={del} onOpenChange={setDel} title="Eliminare l'evento?" description="L'evento e i suoi appunti verranno eliminati definitivamente." onConfirm={remove} />
    </div>
  );
};

export const PersonalEventPanel = ({ panel, onClose, clients, onChanged, onDeleted }) => (
  <Sheet open={!!panel} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="w-full sm:max-w-[960px] bg-[#8FD0FB] overflow-y-auto core-scroll" data-testid="pevent-panel">
      <SheetTitle className="sr-only">Evento personale</SheetTitle>
      <SheetDescription className="sr-only">Modifica l'evento e i tuoi appunti privati</SheetDescription>
      {panel && <EventBody key={panel.key} initial={panel.initial} clients={clients} onChanged={onChanged} onDeleted={(id) => { onDeleted(id); onClose(); }} />}
    </SheetContent>
  </Sheet>
);
